import { UppFloorPlanTable, type UppFloorPlanElement } from '../plan/floor-plan-element';

/**
 * Half of v3 chair height (cm): {@link TABLE_CM.chairHeight} in `render/table.ts`.
 * Label inset is measured from the **visual** footprint; chairs extend this far beyond the tabletop edges.
 */
export const LABEL_INSET_TABLE_CHAIR_OUTDENT_CM = 33 / 2;

/**
 * Axis-aligned bbox (cm) used for **top-right** label badge insets (30 % in from corner).
 * For tables with chairs, expands beyond `xCm`/`widthCm`/`heightCm` so the fraction matches the drawn block.
 */
export function floorPlanElementLabelInsetBBoxCm(el: UppFloorPlanElement): {
  topCm: number;
  rightCm: number;
  widthCm: number;
  heightCm: number;
} {
  const x = el.xCm;
  const y = el.yCm;
  const w = el.widthCm;
  const h = el.heightCm;
  if (el instanceof UppFloorPlanTable && el.chairCount > 0) {
    const o = LABEL_INSET_TABLE_CHAIR_OUTDENT_CM;
    return {
      topCm: y - o,
      rightCm: x + w + o,
      widthCm: w + 2 * o,
      heightCm: h + 2 * o,
    };
  }
  return { topCm: y, rightCm: x + w, widthCm: w, heightCm: h };
}

/**
 * Corners of the same inset rectangle as {@link floorPlanElementLabelInsetBBoxCm}, in **node-local px**
 * (origin top-left of tabletop / element rect, aligned with floor-plan element Konva nodes).
 */
export function floorPlanElementLabelRectCornersLocalPx(
  el: UppFloorPlanElement,
  scalePxPerCm: number,
): Array<{ x: number; y: number }> {
  const w = el.widthCm * scalePxPerCm;
  const h = el.heightCm * scalePxPerCm;
  if (el instanceof UppFloorPlanTable && el.chairCount > 0) {
    const o = LABEL_INSET_TABLE_CHAIR_OUTDENT_CM * scalePxPerCm;
    return [
      { x: -o, y: -o },
      { x: w + o, y: -o },
      { x: w + o, y: h + o },
      { x: -o, y: h + o },
    ];
  }
  return [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
}
